import { useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Row,
  Input,
  List,
  Spin,
  InputRef,
} from "antd";
import { AxiosError } from "axios";
import { Loading3QuartersOutlined } from "@ant-design/icons";
import { CloseIcon, PlusIcon } from "../../../atoms/CustomIcons";
import { updateCompanyFieldsById } from "../ducks/services";
import { useAppDispatch, useAppSelector } from "../../../config/hooks";
import ErrorMessage from "../../../atoms/Toasts/ErrorMessage";
import SuccessMessage from "../../../atoms/Toasts/SuccessMessage";
import { getCompanyFieldsById } from "../ducks/actions";
import { getUserDetails } from "../../MyAccount/ducks/actions";
import { useLocation } from "react-router-dom";
import SettingsGuide from "../../../atoms/SettingsGuide";
import CheckMark from "../../../atoms/CustomIcons/CheckMark";

const antIcon = <Loading3QuartersOutlined spin />;

export default function Department() {
  const dispatch = useAppDispatch();
  const inputRef = useRef<InputRef>(null);
  const [inputs, setInputs] = useState<string[]>([]);
  const [listData, setListData] = useState<string[]>([]);
  const [currentInput, setCurrentInput] = useState<string>("");
  const [addNew, setAddNew] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [openWorksheet, setOpenWorksheet] = useState(false);
  const [defaultTabSelected, setDefaultTabSelected] = useState("");
  const url = useLocation();
  const companyId = url?.pathname.split("/")[2];
  const settingData = useAppSelector((state) => state?.settings?.companyFields);

  const handleAddInput = () => {
    if (currentInput.trim() !== "") {
      setInputs([currentInput, ...inputs]);
      const newListData = [currentInput, ...inputs];
      setLoading(true);
      const payload = {
        companyId: companyId,
        module: "DEPARTMENT",
        fields: newListData,
      };
      updateCompanyFieldsById(payload)
        .then(() => {
          setLoading(false);
          setCurrentInput("");
          setAddNew(false);
          SuccessMessage("Changes saved");
          const payload = {
            companyId: companyId,
            module: "DEPARTMENT",
          };
          dispatch(getCompanyFieldsById(payload));
        })
        .catch((e: unknown) => {
          if (e instanceof AxiosError) {
            setLoading(false);
            ErrorMessage(
              e?.response?.data?.error?.message ?? "Something went wrong"
            );
          } else {
            setLoading(false);
            ErrorMessage("Something went wrong");
          }
        });
    }
  };

  const handleDeleteInput = (index: number) => {
    const newInputs = inputs.filter((_, i) => i !== index);
    setInputs(newInputs);
    setListData(newInputs);

    setLoading(true);
    const payload = {
      companyId: companyId,
      module: "DEPARTMENT",
      fields: newInputs,
    };
    updateCompanyFieldsById(payload)
      .then(() => {
        setLoading(false);
        setCurrentInput("");
        setAddNew(false);
        SuccessMessage("Changes saved");
        const payload = {
          companyId: companyId,
          module: "DEPARTMENT",
        };
        dispatch(getCompanyFieldsById(payload));
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const searchItems = (value: any) => {
    const filteredInputs = inputs.filter((input) =>
      input.toLowerCase().includes(value.toLowerCase())
    );
    setAddNew(false);
    setSearchTerm(value);
    setListData(filteredInputs);
  };

  useEffect(() => {
    if (companyId) {
      const payload = {
        companyId: companyId,
        module: "DEPARTMENT",
      };
      dispatch(getCompanyFieldsById(payload));
    }
  }, [dispatch, companyId]);

  useEffect(() => {
    if (settingData?.listData) {
      const array: string[] = [];
      settingData?.listData?.map((e: { name: string }) => {
        array.push(e?.name);
      });
      setListData(array);
      setInputs(array);
    }
  }, [settingData?.listData]);

  useEffect(() => {
    dispatch(getUserDetails());
  }, [dispatch]);

  const handleAddNew = () => {
    setAddNew(true);
    setTimeout(() => {
      inputRef.current?.focus({
        cursor: "start",
      });
    }, 200);
  };

  return (
    <Spin
      indicator={antIcon}
      size="large"
      spinning={loading || settingData?.loading}
    >
      <Card className="fp_layout_inner">
        <div className="cardContent" style={{ marginBottom: "48px" }}>
          <Form layout="vertical">
            <Row gutter={[0, 24]}>
              <Col span={24}>
                <h4 className="comp_settings_title">Departments</h4>
              </Col>
              <Col span={24}>
                <h4
                  style={{
                    fontSize: "16px",
                    lineHeight: "24px",
                    fontWeight: "300",
                    color: "#000000",
                  }}
                >
                  Functional divisions within your organization that group jobs
                  and roles to achieve specific business objectives.
                </h4>
              </Col>
              <Col span={24} className="Add_Departments">
                <div className="actionBar">
                  <Input
                    value={searchTerm}
                    onChange={(e) => searchItems(e.target.value)}
                    placeholder="Search departments"
                    className="search_departments"
                  />
                  <Button
                    onClick={handleAddNew}
                    className="addNewItem icon_grey_btn"
                  >
                    <PlusIcon /> Add new
                  </Button>
                </div>

                <div className="departments_list">
                  {addNew && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "8px",
                        backgroundColor: "#F3F4F8",
                        borderRadius: "8px",
                        marginBottom: "4px",
                        paddingRight: "10px",
                      }}
                    >
                      <Input
                        value={currentInput}
                        onChange={(e) => setCurrentInput(e.target.value)}
                        placeholder="Type departments name"
                        className="enterNewCompany"
                        ref={inputRef}
                      />
                      <Button
                        onClick={() => setAddNew(false)}
                        className="addDepartmentBtn"
                      >
                        <CloseIcon className="cancelItem" />
                      </Button>
                      <Button
                        onClick={handleAddInput}
                        className="addDepartmentBtn"
                      >
                        <CheckMark className="AddingNewItem" />
                      </Button>
                    </div>
                  )}

                  <List
                    dataSource={listData}
                    locale={{
                      emptyText: !addNew
                        ? searchTerm
                          ? "No results found."
                          : "There are no departments added yet."
                        : " ",
                    }}
                    renderItem={(item: string, index: number) => (
                      <List.Item
                        actions={[
                          <Button
                            type="link"
                            onClick={() => handleDeleteInput(index)}
                            icon={<CloseIcon />}
                            className="deleteBtn"
                          />,
                        ]}
                      >
                        {item}
                      </List.Item>
                    )}
                  />
                </div>
              </Col>
            </Row>
          </Form>

          <SettingsGuide
            openWorksheet={openWorksheet}
            setOpenWorksheet={setOpenWorksheet}
            defaultTabSelected={defaultTabSelected}
            setDefaultTabSelected={setDefaultTabSelected}
          />
        </div>
      </Card>
    </Spin>
  );
}
